import React, { useCallback, useEffect, useState } from 'react';
import {
	geographyOptions,
	zipCodeOptions,
	housingOptionsInitalData,
} from '@mockData';
import { CustomSelector, ZipCodeInput } from '@components';
import { districtOptionsData } from '@interface';
import {
	categoryValueSelector,
	getAddFilterParams,
	getHousingParams,
	getHousingValue,
} from '@utils';
import { debounce } from 'lodash';
import { Category, Filter, FilterObject } from '@interface';
import {
	FilterComponent,
	PropertyAddFilter,
} from '@/./pages/customSearch/components';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomSearchOptionState } from '@/store/slice';
import { RootState } from '@store';

export const CustomSearch: React.FC<{
	module?: string;
	id?: string;
	navigateFunction?: NavigateFunction; // External navigate function
	onSearchSubmit?: (url: string) => void; // Optional callback for parent component
	isNested?: boolean; // Flag to indicate if component is used inside another component
}> = ({
	module = '',
	id = '',
	navigateFunction,
	onSearchSubmit,
	isNested = false,
}) => {
	const routerNavigate = useNavigate();
	const navigate = navigateFunction || routerNavigate;
	const [selectedGeography, setSelectedGeography] = useState('');
	const [geographyValue, setGeographyValue] = useState('');
	const [geographyList, setGeographyList] = useState<districtOptionsData[]>([]);
	const [zipCodeToggle, setZipCodeToggle] = useState<boolean>(false);
	const [selectedHousingType, setSelectedHousingType] =
		useState<Category>('all');
	const [housingfilters, setHousingFilters] = useState<Filter[]>([]);
	const [programSelectOpen, setProgramSelectOpen] = useState(false);
	const [addfilters, setAddFilters] = useState<FilterObject[]>([]);
	const [logicalOperator, setLogicalOperator] = useState<'AND' | 'OR'>('AND');
	const [showAddFilterDropdown, setShowAddFilterDropdown] =
		useState<boolean>(false);
	const labelClass = 'block text-[18px] font-normal  mb-2';
	const divSelectorClass = 'flex flex-col md:align-middle  w-full md:w-60';
	const dispatch = useDispatch();
	const { SelectedHousingType, Addfilters, Housingfilters, LogicalOperator } =
		useSelector(
			(state: RootState) => state.rootSlice.application.customSearchOptionState
		);

	useEffect(() => {
		if (module) {
			setSelectedGeography(module);
			const options = categoryValueSelector(module);
			if (options.length > 0) {
				setGeographyList(options);
			}
		}
		if (id) {
			setGeographyValue(id);
		}
		if (Addfilters.length > 0) {
			setAddFilters(Addfilters);
		}
		if (Housingfilters.length > 0) {
			setHousingFilters(Housingfilters);
		}
		if (LogicalOperator) {
			setLogicalOperator(LogicalOperator);
		}
		if (SelectedHousingType) {
			setSelectedHousingType(SelectedHousingType);
		}
	}, [
		module,
		id,
		SelectedHousingType,
		Addfilters,
		Housingfilters,
		LogicalOperator,
	]);



	const fetchResults = async (query: string) => {
		if (!query) {
			setGeographyList(zipCodeOptions);
			setZipCodeToggle(true);
			return;
		}
		const mockData = zipCodeOptions.filter((item) =>
			item.value.includes(query.toLowerCase())
		);
		setGeographyList(mockData);
		setZipCodeToggle(true);
	};

	const debouncedFetchResults = useCallback(debounce(fetchResults, 500), []);

	const handleSelectgeographyChange = (value: string) => {
		setGeographyList([]);
		setGeographyValue('');
		setSelectedGeography(value);
		const options = categoryValueSelector(value);
		if (options.length > 0) {
			setGeographyList(options);
		}
	};

	const handleGeoGraphyValueChange = (value: string) => {
		setGeographyValue(value);
	};

	const handleZipCodeChange = (value: string) => {
		setGeographyValue(value);
		debouncedFetchResults(value);
	};

	const zipCodeSelector = (val: string) => {
		setGeographyValue(val);
		setZipCodeToggle(false);
	};

	const handleHousnigTypeChange = (val: Category) => {
		setSelectedHousingType(val);
	};

	const onFinalSubmit = async () => {
		const currentModule = selectedGeography;
		const currentId = selectedGeography === 'city' ? 'NYC' : geographyValue;
		const housingValue = getHousingValue(selectedHousingType);
		const  housingParams  =
			housingfilters.length > 0
				? await getHousingParams(housingfilters)
				: {} ;
		const addFiterParams =
			addfilters.length > 0
				? getAddFilterParams(addfilters, logicalOperator)
				: {};
		
		const params = {
			...housingValue,
			...housingParams,
			...addFiterParams,
			...(currentModule === 'borough' && { borough: currentId }),
		};

		const queryString = new URLSearchParams(params).toString();
		const navigateParam = `/${currentModule}/${currentId}?${queryString}`;
		const navigationUrl = `/search${navigateParam}`;

		// Save the search state in Redux
		dispatch(
			setCustomSearchOptionState({
				SelectedHousingType: selectedHousingType,
				Housingfilters: housingfilters,
				Addfilters: addfilters,
				LogicalOperator: logicalOperator,
				navigateParam,
			})
		);

		// If we have a callback for nested usage, use that
		if (isNested && onSearchSubmit) {
			onSearchSubmit(navigationUrl);
			return;
		}

		// Otherwise use the appropriate navigate function
		try {
			navigate(navigationUrl, { replace: isNested }); // Use replace for nested to avoid navigation stack issues
		} catch (error) {
			console.error('Navigation failed:', error);
		}
	};

	return (
		<div className="p-4 md:px-12">
			<label className={labelClass}>1) Select a geography</label>
			{selectedGeography === 'city' && (
				<div>
					<p className="text-[14px] text-[#dc3545]">
						Warning: Some borough and citywide queries may take a long time to
						complete. Please allow up to 2 minutes for your query to finish.
					</p>
				</div>
			)}
			<div className={divSelectorClass}>
				<div>
					<CustomSelector
						value={selectedGeography}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
							handleSelectgeographyChange(e.target.value)
						}
						list={geographyOptions}
						label={''}
						labelClassName=""
						customDivStyle="flex flex-grow w-auto"
					/>
				</div>
				{selectedGeography && geographyList.length > 0 && (
					<div>
						{selectedGeography !== 'zipcode' ? (
							<CustomSelector
								value={geographyValue}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
									handleGeoGraphyValueChange(e.target.value)
								}
								list={geographyList}
								label={''}
								labelClassName=""
								customDivStyle="flex flex-grow w-auto"
							/>
						) : (
							<ZipCodeInput
								value={geographyValue}
								onChange={handleZipCodeChange}
								toggleDropdown={zipCodeToggle}
								setToggleDropdown={setZipCodeToggle}
								results={geographyList}
								onSelect={zipCodeSelector}
							/>
						)}
					</div>
				)}
			</div>

			{(selectedGeography === 'city' ||
				(selectedGeography && geographyValue)) && (
				<div className="mt-4">
					<label className={labelClass}>2) Add filters</label>
					<div className={divSelectorClass}>
						<div>
							<CustomSelector
								value={selectedHousingType}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
									handleHousnigTypeChange(e.target.value as Category)
								}
								list={housingOptionsInitalData}
								label={''}
								labelClassName=""
								customDivStyle="flex flex-grow w-auto"
							/>
						</div>
					</div>
					<FilterComponent
						category={selectedHousingType}
						filters={housingfilters}
						setFilters={setHousingFilters}
						programSelectOpen={programSelectOpen}
						setProgramSelectOpen={setProgramSelectOpen}
					/>
				</div>
			)}

			{(selectedGeography === 'city' ||
				(selectedGeography && geographyValue)) && (
				<div className="mt-4">
					<label className={labelClass}>3) Add filters</label>
					<PropertyAddFilter
						filters={addfilters}
						setFilters={setAddFilters}
						logicalOperator={logicalOperator}
						setLogicalOperator={setLogicalOperator}
						showFilterDropdown={showAddFilterDropdown}
						setShowFilterDropdown={setShowAddFilterDropdown}
					/>
				</div>
			)}

			{(selectedGeography === 'city' ||
				(selectedGeography && geographyValue)) && (
				<div className="mt-4">
					<button
						onClick={onFinalSubmit}
						className="min-w-[240px] max-w-[240px] btn-dark p-2 border-[#324B69] rounded-sm"
					>
						Submit
					</button>
				</div>
			)}
		</div>
	);
};
